import { isEqual } from 'lodash';
import { CSSProperties, useCallback, useContext } from 'react';
import { Icon } from '@shared/components/Icon';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import {
  TimeTrackerButton,
  TimerText,
  calculateDurationFromTimeTrackerData,
  isTimerRunning,
} from '@shared/components/timer';
import {
  TimeTrackerComponentData,
  TimeTrackerField,
} from '@shared/data/entity/fields/TimeTrackerField';
import { useFieldDraft } from '@shared/scenes/entity/components/fields/useFieldDraft';
import { useUpdateWorkflowEntity } from '@shared/scenes/entity/edit/hooks/useUpdateWorkflowEntity';
import { ColorThemeContext, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { useBoolean } from '@shared/util/hooks';
import { CardCollectionViewFieldContainer } from './CardCollectionViewFieldContainer';

//#region Public Interface

interface Props {
  component: API.TimeTrackerComponent;
  entityId: string;
  field: TimeTrackerField;
  canUpdateCard: boolean;
  showIcon?: boolean;
}

export function TimeTrackerCollectionViewField({
  component,
  entityId,
  field,
  canUpdateCard,
  showIcon,
}: Props) {
  const colors = useContext(ColorThemeContext);

  const trackedData = field.timerEntries;
  const [draft, commitDraft] = useFieldDraft<TimeTrackerComponentData, TimeTrackerComponentData>(
    trackedData,
    isEqual,
  );
  const data = draft ?? trackedData;
  const isRunning = isTimerRunning(data);

  return (
    <CardCollectionViewFieldContainer fieldType={field.fieldType}>
      <View style={{ alignItems: 'stretch' }}>
        <View
          row
          gap={0.5}
          centerVertically
          paddingLeft={showIcon ? 0.1 : 0}
          paddingRight={showIcon ? 1.25 : 0}
          style={{ justifyContent: 'flex-start' }}
        >
          {showIcon && <Icon name="time-stopwatch" gridSize={3.5} color={colors.accent} />}
          <Text size="body1">
            <TimerText
              isRunning={isRunning}
              onTick={(now: Date) => calculateDurationFromTimeTrackerData(data, now)}
            />
          </Text>
        </View>
        {!component.readonly && canUpdateCard && (
          <View stopPropagation>
            <FieldUpdatingTimeTrackerButton
              entityId={entityId}
              component={component}
              data={data}
              isRunning={isRunning}
              commitDraft={commitDraft}
              style={{
                marginInline: showIcon ? 0 : -grid(1.25),
                marginTop: grid(1),
              }}
            />
          </View>
        )}
      </View>
    </CardCollectionViewFieldContainer>
  );
}

//#endregion

//#region Private Interface

function FieldUpdatingTimeTrackerButton({
  entityId,
  component,
  data,
  isRunning,
  commitDraft,
  style,
}: {
  entityId: string;
  component: API.TimeTrackerComponent;
  data: TimeTrackerComponentData;
  isRunning: boolean;
  commitDraft: (draft: TimeTrackerComponentData, committed: TimeTrackerComponentData) => void;
  style?: CSSProperties;
}) {
  const { updateWorkflowEntity } = useUpdateWorkflowEntity();
  const [loading, startLoading, stopLoading] = useBoolean(false);

  const handleDataChanged = useCallback(
    async (newData: TimeTrackerComponentData) => {
      commitDraft(newData, newData);
      startLoading();
      try {
        await updateWorkflowEntity(entityId, {
          [component.id]: newData,
        });
      } finally {
        stopLoading();
      }
    },
    [entityId, component.id, updateWorkflowEntity, commitDraft, startLoading, stopLoading],
  );

  return (
    <TimeTrackerButton
      component={component}
      data={data}
      loading={loading}
      isRunning={isRunning}
      style={style}
      onDataChanged={handleDataChanged}
    />
  );
}

//#endregion
